import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { EnvelopeSimple, House, FileText, CreditCard } from "@phosphor-icons/react";
import { PlanBadge, UserMenu } from "@/components/header/UserMenu";

const NavLink = ({ to, label, icon: Icon, current }) => (
  <Link
    to={to}
    data-testid={`nav-${to.replace("/", "") || "home"}`}
    className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-xs uppercase tracking-widest transition-colors border ${
      current
        ? "border-foreground bg-foreground text-background"
        : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
    }`}
  >
    {Icon && <Icon size={12} weight="bold" />}
    {label}
  </Link>
);

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <header
      className="sticky top-0 z-30 bg-background border-b border-border"
      data-testid="app-header"
    >
      <div className="max-w-screen-2xl mx-auto px-6 h-16 flex items-center gap-4">
        <Link to={user ? "/app" : "/"} className="flex items-center gap-2 mr-4" data-testid="logo-link">
          <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-heading font-black tracking-tighter text-lg">
            SF
          </div>
          <div className="hidden sm:block">
            <div className="font-heading font-black text-base tracking-tight leading-none">
              STORY FORGE
            </div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
              backlog groomer
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-1 flex-1" data-testid="header-nav">
          {user ? (
            <NavLink
              to="/app"
              label="Stories"
              icon={FileText}
              current={path === "/app" || path.startsWith("/story/")}
            />
          ) : (
            <NavLink to="/" label="Home" icon={House} current={path === "/"} />
          )}
          <NavLink to="/pricing" label="Pricing" icon={CreditCard} current={path === "/pricing"} />
          <NavLink to="/contact" label="Contact" icon={EnvelopeSimple} current={path === "/contact"} />
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <PlanBadge user={user} />
              <UserMenu user={user} onLogout={handleLogout} />
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="rounded-none border-2 h-9 font-mono text-xs uppercase tracking-widest"
                data-testid="header-login-btn"
              >
                Sign in
              </Button>
              <Button
                onClick={() => navigate("/signup")}
                className="rounded-none bg-black text-white hover:bg-[hsl(var(--accent))] h-9 font-mono text-xs uppercase tracking-widest"
                data-testid="header-signup-btn"
              >
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
